import {
  Snackbar as SnackbarBase,
  SnackbarContent,
  Stack,
  Typography,
  type SnackbarCloseReason,
} from '@mui/material';
import { memo } from 'react';
import { Icon, type IconName } from '../icon';
import type { SnackbarProps as Props, SnackbarSeverity } from './types';

const SEVERITY_ICON: Record<SnackbarSeverity, IconName> = {
  default: 'info-circle-outline',
  success: 'check-circle-outline',
  warning: 'alert',
  error: 'info-circle-outline',
};

export const Snackbar = memo(
  ({
    open,
    onClose,
    severity = 'default',
    variant = 'filled',
    title,
    message,
    icon,
    actionButton,
    hideClose,
    ...other
  }: Props) => {
    const handleClose = (
      _event: React.SyntheticEvent | Event,
      reason: SnackbarCloseReason
    ) => {
      if (reason === 'clickaway') return;
      onClose?.();
    };

    const startIconName =
      icon === false ? null : icon ?? SEVERITY_ICON[severity];
    const startIconColor = severity === 'default' ? 'primary.500' : 'inherit';

    const messageNode = (
      <>
        {startIconName && (
          <Icon name={startIconName} size={20} color={startIconColor} />
        )}
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Typography variant="body3" fontWeight={700} color="inherit">
              {title}
            </Typography>
          )}
          {message && (
            <Typography variant="body4" color="inherit">
              {message}
            </Typography>
          )}
        </Stack>
      </>
    );

    let action: React.ReactNode = actionButton ?? null;
    if (!actionButton && !hideClose && onClose) {
      action = (
        <Icon name="close" size={18} color="inherit" onClick={onClose} />
      );
    }

    return (
      <SnackbarBase open={open} onClose={handleClose} {...other}>
        <SnackbarContent
          severity={severity}
          variant={variant}
          message={messageNode}
          action={action}
        />
      </SnackbarBase>
    );
  }
);

Snackbar.displayName = 'Snackbar';
